import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid, Cell,
} from "recharts";

const fmt = (v) => `${(v || 0).toFixed(2)} $`;
const fmtCompact = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toFixed(0);
};

/**
 * Batch #14.79 — Widget compact : 2 graphiques côte à côte
 *   - Revenus vs Dépenses (LineChart)
 *   - État des paiements (BarChart)
 */
export function DashboardCharts({ summary, year }) {
  const navigate = useNavigate();
  const lineData = useMemo(() => {
    const monthly = summary?.monthly_revenue || [];
    return monthly.map(m => ({
      month: m.month?.slice(5) || m.month, // MM
      Revenus: Number(m.revenue || 0),
      Dépenses: Number(m.expenses || 0),
    }));
  }, [summary]);

  const unpaidData = useMemo(() => {
    const paid = Number(summary?.total_paid || 0);
    const unpaid = Number(summary?.total_unpaid || 0);
    const overdue = Number(summary?.overdue_amount || 0);
    return [
      { label: "Payé", value: paid, fill: "#10b981" },
      { label: "Impayé", value: unpaid, fill: "#f59e0b" },
      { label: "En retard", value: overdue, fill: "#ef4444" },
    ];
  }, [summary]);

  return (
    <section data-testid="dashboard-charts" className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {/* Revenus vs Dépenses */}
      <div className="bg-white border rounded-xl p-3" data-testid="chart-revenue-vs-expenses">
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-xs font-semibold text-gray-700">Revenus vs Dépenses</h3>
          <button
            onClick={() => navigate("/admin/reports")}
            className="text-[10px] text-blue-600 hover:underline"
          >
            Détails →
          </button>
        </div>
        {lineData.length === 0 ? (
          <div className="h-[160px] flex items-center justify-center text-gray-400 text-xs">
            Pas de données pour {year || "cette période"}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={lineData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
              <XAxis dataKey="month" tick={{ fontSize: 9 }} />
              <YAxis tick={{ fontSize: 9 }} tickFormatter={fmtCompact} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line type="monotone" dataKey="Revenus" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="Dépenses" stroke="#ef4444" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* État des paiements */}
      <div className="bg-white border rounded-xl p-3" data-testid="chart-unpaid">
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-xs font-semibold text-gray-700">État des paiements</h3>
          <button
            onClick={() => navigate("/admin/comptabilite/transactions-clients")}
            className="text-[10px] text-blue-600 hover:underline"
          >
            Voir factures →
          </button>
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={unpaidData} layout="vertical" margin={{ top: 4, right: 8, left: 16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
            <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={fmtCompact} />
            <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={64} />
            <Tooltip formatter={(v) => fmt(v)} />
            <Bar dataKey="value" radius={[0, 8, 8, 0]}>
              {unpaidData.map((entry) => (
                <Cell key={entry.label} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

/** Tiny inline sparkline for KPI cards. */
export function Sparkline({ data, color = "#3b82f6", height = 28 }) {
  if (!data || data.length < 2) return null;
  const points = data.map((d, i) => ({ x: i, y: Number(d) || 0 }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={points}>
        <Line type="monotone" dataKey="y" stroke={color} strokeWidth={1.5} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}
